import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConversationListFilter, ConversationThreadView } from '@scoutaround/script-breakdown-shared';

import { api, type ProjectConversation } from '../../lib/api.js';
import { NewThreadForm } from '../../components/conversations/NewThreadForm.js';
import { ThreadList } from '../../components/conversations/ThreadList.js';
import { ThreadView } from '../../components/conversations/ThreadView.js';
import { useConversationsEnabled, useHostMessagesEnabled } from '../../components/conversations/hooks.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { SplitWorkspace } from '../../components/ui/SplitWorkspace.js';
import { SearchField } from '../../components/ui/SearchField.js';
import { SegmentedFilter } from '../../components/ui/SegmentedFilter.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import '../../styles/conversations.css';

/** The context is what the conversation is ABOUT, said plainly. "location" and
 *  "booking" are the storage's words, not a producer's. */
const CONTEXT_LABEL: Record<ProjectConversation['contextType'], string> = {
  location: 'About a location on this project',
  booking: 'About a booking on this project',
};

const FILTERS: readonly { key: ConversationListFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'following', label: 'Following' },
  { key: 'mentions', label: 'Mentions' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'removed', label: 'Removed items' },
];

/**
 * THE CONVERSATIONS TAB (ADR 0021): every team conversation this person may read,
 * on the production and on its scenes, script locations, places and breakdown
 * elements, with the one open in a pane beside the list (`/conversations/:id`).
 * A conversation on a removed item stays findable here, under its own filter.
 *
 * Below it, while host messaging exists on the platform, the production's
 * conversations with hosts — still a LENS on the platform inbox, not a second one
 * (`script-breakdown/CLAUDE.md`). While host messaging is off the section is not
 * shown at all, rather than an empty list that can never fill.
 */
export function ChatTab({
  projectId,
  threadId,
  onOpenThread,
}: {
  projectId: string;
  threadId?: string | null;
  onOpenThread?: (threadId: string | null) => void;
}) {
  const conversationsOn = useConversationsEnabled();
  const hostMessagesOn = useHostMessagesEnabled();

  return (
    <div data-testid="chat-tab" style={{ display: 'grid', gap: 28 }}>
      {conversationsOn ? (
        <TeamConversations projectId={projectId} threadId={threadId ?? null} onOpenThread={onOpenThread ?? (() => undefined)} />
      ) : null}
      {hostMessagesOn ? <HostMessages projectId={projectId} /> : null}
      {!conversationsOn && !hostMessagesOn ? (
        <p className="mm" data-testid="chat-unavailable" style={{ margin: 0, fontSize: 13 }}>
          Conversations are not available on this production yet.
        </p>
      ) : null}
    </div>
  );
}

function TeamConversations({
  projectId,
  threadId,
  onOpenThread,
}: {
  projectId: string;
  threadId: string | null;
  onOpenThread: (threadId: string | null) => void;
}) {
  const [filter, setFilter] = useState<ConversationListFilter>('all');
  const [query, setQuery] = useState('');
  const [starting, setStarting] = useState(false);
  const [extra, setExtra] = useState<ConversationThreadView[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['conversations', projectId, 'tab', filter],
    queryFn: () => api.conversations(projectId, { filter }),
    refetchInterval: 30_000,
  });

  useEffect(() => {
    setExtra([]);
    setCursor(null);
  }, [filter]);

  const threads = [...(list.data?.threads ?? []), ...extra];
  const next = cursor ?? list.data?.nextCursor ?? null;
  // The search narrows what is loaded: titles and what each one is about.
  const needle = query.trim().toLowerCase();
  const shown =
    needle === ''
      ? threads
      : threads.filter((t) => `${t.title ?? ''} ${t.anchorType === 'PROJECT' ? 'whole production' : t.anchorLabel}`.toLowerCase().includes(needle));

  const startNew = () => {
    setStarting(true);
    onOpenThread(null);
  };

  return (
    <section data-testid="conversations-tab" className="cv-page">
      <PageHeader
        title="Conversations"
        lede="Discuss scenes, locations and production decisions with your team."
        actions={
          <button type="button" className="btn btn-primary" data-testid="conversations-new" onClick={startNew}>
            New conversation
          </button>
        }
      />

      <SplitWorkspace
        testId="conversations-split"
        navLabel="Conversations"
        backLabel="All conversations"
        showDetail={starting || threadId !== null}
        onBackToList={() => {
          setStarting(false);
          onOpenThread(null);
        }}
        nav={
          <>
            <div className="ui-nav-head">
              <SearchField
                value={query}
                onChange={setQuery}
                placeholder="Search conversations"
                label="Search conversations"
                testId="conversations-search"
              />
              <SegmentedFilter
                options={FILTERS.map((f) => ({ id: f.key, label: f.label }))}
                value={filter}
                onChange={setFilter}
                label="Show"
                testIdPrefix="conversations-filter"
              />
            </div>
            <div className="ui-nav-list" data-testid="conversations-list-scroller">
              {list.isPending ? <p className="mm cv-list-empty">Loading…</p> : null}
              {list.isError ? (
                <p className="cv-list-empty" role="alert" data-testid="conversations-list-error">
                  The conversations could not be loaded.{' '}
                  <button
                    type="button"
                    className="ctl"
                    onClick={() => {
                      void list.refetch();
                    }}
                  >
                    Try again
                  </button>
                </p>
              ) : null}
              {list.data ? (
                <ThreadList
                  testId="conversations-list"
                  threads={shown}
                  showAnchor
                  activeId={threadId}
                  emptyText={
                    needle !== ''
                      ? 'No matching conversations.'
                      : filter === 'all'
                        ? 'Start a conversation with your production team.'
                        : 'Nothing here.'
                  }
                  onOpen={(id) => {
                    setStarting(false);
                    onOpenThread(id);
                  }}
                />
              ) : null}
              {next ? (
                <button
                  type="button"
                  className="ctl"
                  data-testid="conversations-more"
                  style={{ margin: 12 }}
                  onClick={() => {
                    void api.conversations(projectId, { filter, cursor: next }).then((page) => {
                      setExtra((current) => [...current, ...page.threads]);
                      setCursor(page.nextCursor ?? '');
                    });
                  }}
                >
                  Load more
                </button>
              ) : null}
            </div>
            <div className="ui-nav-foot">
              <EmailSetting projectId={projectId} />
            </div>
          </>
        }
        detail={
          <div className="cv-pane">
            {starting ? (
              <div className="cv-pane-pad">
                <NewThreadForm
                  projectId={projectId}
                  anchorType="PROJECT"
                  anchorId={projectId}
                  onCancel={() => {
                    setStarting(false);
                  }}
                  onCreated={(thread) => {
                    setStarting(false);
                    onOpenThread(thread.id);
                  }}
                />
              </div>
            ) : threadId ? (
              <ThreadView
                key={threadId}
                projectId={projectId}
                threadId={threadId}
                layout="pane"
                onClosed={() => {
                  onOpenThread(null);
                }}
              />
            ) : (
              <EmptyState
                testId="conversations-pick"
                title={threads.length === 0 && filter === 'all' && list.data !== undefined ? 'No conversations yet' : 'Select a conversation to read it.'}
                action={
                  threads.length === 0 && filter === 'all' && list.data !== undefined ? (
                    <button type="button" className="ctl" data-testid="conversations-start-first" onClick={startNew}>
                      Start a conversation with your production team
                    </button>
                  ) : undefined
                }
              >
                {threads.length === 0 && filter === 'all' && list.data !== undefined
                  ? 'Start one for the whole production here, or from Discuss on a scene, a location or an element.'
                  : undefined}
              </EmptyState>
            )}
          </div>
        }
      />
    </section>
  );
}

/** One switch per production: the email digest, or none. In-app counts are unchanged. */
function EmailSetting({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const highlight = params.get('settings') === 'email';
  const box = useRef<HTMLLabelElement | null>(null);

  const email = useQuery({
    queryKey: ['conversation-email', projectId],
    queryFn: () => api.conversationEmail(projectId),
  });
  const save = useMutation({
    mutationFn: (on: boolean) => api.setConversationEmail(projectId, on ? 'DIGEST' : 'OFF'),
    onSuccess: (answer) => {
      queryClient.setQueryData(['conversation-email', projectId], answer);
    },
  });

  useEffect(() => {
    if (highlight) box.current?.scrollIntoView({ block: 'center' });
  }, [highlight]);

  if (!email.data) return null;

  return (
    <label ref={box} className="cv-email" data-highlight={highlight ? '1' : undefined}>
      <input
        type="checkbox"
        data-testid="conversations-email-toggle"
        checked={email.data.email === 'DIGEST'}
        disabled={save.isPending}
        onChange={(event) => {
          save.mutate(event.target.checked);
        }}
      />
      <span>
        Email me a summary of conversations I follow
        <span className="cv-email-note">Counts and titles only, never the messages.</span>
      </span>
    </label>
  );
}

/**
 * The production's conversations with hosts — a LENS on the platform inbox, not a
 * second messaging system: it answers which of the person's own conversations are
 * about THIS production and sends them to the real thread to read and reply.
 */
function HostMessages({ projectId }: { projectId: string }) {
  const conversations = useQuery({
    queryKey: ['chat', projectId],
    queryFn: () => api.listConversations(projectId),
  });

  return (
    <section data-testid="host-messages">
      <p className="dh" style={{ fontSize: 18, margin: 0 }}>
        Messages with hosts
      </p>
      <p className="mm" style={{ fontSize: 13, margin: '8px 0 0', lineHeight: 1.6, maxWidth: '68ch' }}>
        Your conversations with hosts about this production, gathered in one place. Messages
        themselves live in your ScoutAround inbox — this is a view of it, so replying means opening
        the real thread.
      </p>

      {conversations.isPending ? (
        <p className="mm" style={{ fontSize: 13, marginTop: 18 }}>
          Loading…
        </p>
      ) : null}

      {conversations.data?.length === 0 ? (
        <p className="mm" style={{ fontSize: 13, marginTop: 18 }} data-testid="chat-empty">
          No conversations with hosts about this project yet. One starts when you message a host
          about a location you have added here.
        </p>
      ) : null}

      <ol
        style={{
          listStyle: 'none',
          margin: '18px 0 0',
          padding: 0,
          display: 'grid',
          gap: 1,
          background: 'var(--color-divider)',
          border: conversations.data?.length ? '1px solid var(--color-divider)' : 'none',
        }}
      >
        {conversations.data?.map((c) => (
          <li
            key={c.threadId}
            data-testid={`chat-thread-${String(c.threadId)}`}
            style={{
              background: 'var(--color-bg)',
              padding: '12px 16px',
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'baseline',
            }}
          >
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>{CONTEXT_LABEL[c.contextType]}</span>
            {c.unread ? (
              <span className="mm" style={{ fontSize: 11, fontWeight: 600 }} data-testid={`chat-unread-${String(c.threadId)}`}>
                Unread
              </span>
            ) : null}
            <span className="mm" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
              {c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleString() : 'No messages yet'}
            </span>
            {/* A full page load on purpose: the inbox is a different app on a
                different host, so a router link would go nowhere. */}
            <a className="ctl" href={c.href} style={{ fontSize: 12 }} data-testid={`chat-open-${String(c.threadId)}`}>
              Open conversation
            </a>
          </li>
        ))}
      </ol>
    </section>
  );
}
